import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar'; // Import Calendar components
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Import Calendar CSS
import { Spin, Alert, Button, Card, Modal, Form, Input, Select, message, Row, Col, AutoComplete, Space, Typography, Table, Tag, Radio } from 'antd'; // Keep AntD components for Modal/Form
import { PlusOutlined, SearchOutlined, ClearOutlined, CalendarOutlined, UserOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Option } = Select;
const { RangePicker } = MomentDatePicker;
const { TextArea } = Input;
const { Search } = Input;
const { Text } = Typography;

const localizer = momentLocalizer(moment); // Setup moment localizer for react-big-calendar

const AppointmentScheduler = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const { user } = useAuth(); // For scheduledBy and role checks

  // State for searching patients/providers in the modal form
  const [patientOptions, setPatientOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // State for initial lists to show when dropdown opens
  const [allPatients, setAllPatients] = useState([]);
  const [allProviders, setAllProviders] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // State for search and filter functionality
  const [patientSearchText, setPatientSearchText] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [searchAndFilterApplied, setSearchAndFilterApplied] = useState(false);

  // State for view mode (calendar vs patient list)
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'patient'

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all appointments for now - add filtering later
      const response = await axios.get('/appointments'); // Removed prefix
      const appointmentsData = response.data.data.appointments;
      setAppointments(appointmentsData);
      setFilteredAppointments(appointmentsData);

      // Extract unique providers from appointments for filter dropdown
      const providers = [];
      const providerMap = new Map();
      appointmentsData.forEach(appointment => {
        if (appointment.provider && !providerMap.has(appointment.provider._id)) {
          providerMap.set(appointment.provider._id, {
            id: appointment.provider._id,
            name: appointment.provider.name || 'Unknown Provider',
            role: appointment.provider.role || ''
          });
          providers.push(providerMap.get(appointment.provider._id));
        }
      });
      setAvailableProviders(providers);
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError(err.response?.data?.message || 'Failed to fetch appointments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchInitialData();
  }, [fetchAppointments]);

  // Fetch initial data for dropdowns
  const fetchInitialData = async () => {
    try {
      // Fetch all patients (first 50 for dropdown)
      const patientsResponse = await axios.get('/patients?limit=50');
      const patients = patientsResponse.data.data.patients.map(p => ({
        value: p._id,
        label: `${p.lastName}${p.middleName ? ' ' + p.middleName : ''} ${p.firstName} (DOB: ${moment(p.dateOfBirth).format('YYYY-MM-DD')})`
      }));
      setAllPatients(patients);
      setPatientOptions(patients); // Show all patients initially

      // Fetch all providers
      const providersResponse = await axios.get('/users?role=MD&role=Nursing&limit=50');
      const providers = providersResponse.data.data.users.map(u => ({
        value: u._id,
        label: `${u.name} (${u.role})`
      }));
      setAllProviders(providers);
      setProviderOptions(providers); // Show all providers initially

      // Set predefined appointment types
      const types = [
        'Regular Checkup',
        'Follow-up Visit',
        'Consultation',
        'Emergency',
        'Procedure',
        'Lab Work',
        'Vaccination',
        'Physical Therapy',
        'Mental Health',
        'Specialist Consultation'
      ];
      setAppointmentTypes(types);

    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  // Search and filter functionality
  const applySearchAndFilters = useCallback(() => {
    let filtered = [...appointments];

    // Apply patient name search
    if (patientSearchText.trim()) {
      const searchLower = patientSearchText.toLowerCase().trim();
      filtered = filtered.filter(appointment => {
        if (!appointment.patient) return false;
        const fullName = `${appointment.patient.firstName || ''} ${appointment.patient.middleName || ''} ${appointment.patient.lastName || ''}`.toLowerCase();
        return fullName.includes(searchLower);
      });
    }

    // Apply provider filter
    if (providerFilter !== 'all') {
      filtered = filtered.filter(appointment =>
        appointment.provider && appointment.provider._id === providerFilter
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.status === statusFilter);
    }

    setFilteredAppointments(filtered);
    setSearchAndFilterApplied(patientSearchText.trim() !== '' || providerFilter !== 'all' || statusFilter !== 'all');
  }, [appointments, patientSearchText, providerFilter, statusFilter]);

  useEffect(() => {
    applySearchAndFilters();
  }, [applySearchAndFilters]);

  // Handle search input
  const handlePatientSearchChange = (value) => {
    setPatientSearchText(value);
  };

  // Handle provider filter change
  const handleProviderFilterChange = (value) => {
    setProviderFilter(value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setPatientSearchText('');
    setProviderFilter('all');
    setStatusFilter('all');
  };

  // Handle view mode change
  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
  };

  // Group appointments by patient for patient view
  const getPatientGroupedAppointments = () => {
    const grouped = {};
    filteredAppointments.forEach(appointment => {
      if (appointment.patient) {
        const patientKey = appointment.patient._id;
        if (!grouped[patientKey]) {
          grouped[patientKey] = {
            patient: appointment.patient,
            appointments: []
          };
        }
        grouped[patientKey].appointments.push(appointment);
      }
    });

    // Convert to array and sort appointments within each patient group
    return Object.values(grouped).map(group => ({
      ...group,
      appointments: group.appointments.sort((a, b) =>
        moment(a.startTime).unix() - moment(b.startTime).unix()
      )
    })).sort((a, b) => {
      const nameA = `${a.patient.lastName} ${a.patient.firstName}`.toLowerCase();
      const nameB = `${b.patient.lastName} ${b.patient.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Format appointments for the Calendar component with proper date handling (use filtered appointments)
  const calendarEvents = filteredAppointments.map(app => {
    // Ensure proper date parsing and timezone handling
    const startTime = moment(app.startTime).toDate();
    const endTime = moment(app.endTime).toDate();

    // Debug logging for date conversion issues
    if (process.env.NODE_ENV === 'development') {
      console.log('Appointment date conversion:', {
        originalStart: app.startTime,
        originalEnd: app.endTime,
        convertedStart: startTime,
        convertedEnd: endTime,
        patient: app.patient?.firstName + ' ' + app.patient?.lastName
      });
    }

    return {
      id: app._id, // Add unique ID for calendar events
      title: `${app.patient?.lastName || ''}${app.patient?.middleName ? ' ' + app.patient.middleName : ''} ${app.patient?.firstName || ''} w/ ${app.provider?.name || 'N/A'} (${app.appointmentType})`,
      start: startTime,
      end: endTime,
      resource: app, // Store the original appointment object
      // Add additional properties for better calendar display
      status: app.status,
      appointmentType: app.appointmentType,
      allDay: false, // Ensure this is not treated as an all-day event
    };
  }).filter(event => {
    // Filter out events with invalid dates
    return event.start instanceof Date && event.end instanceof Date &&
           !isNaN(event.start.getTime()) && !isNaN(event.end.getTime()) &&
           event.start < event.end; // Ensure start is before end
  });

  // Table columns for patient view
  const patientViewColumns = [
    {
      title: 'Patient',
      key: 'patient',
      render: (_, record) => (
        <div>
          <Text strong>
            {record.patient.lastName}{record.patient.middleName ? ` ${record.patient.middleName}` : ''} {record.patient.firstName}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            DOB: {moment(record.patient.dateOfBirth).format('MM/DD/YYYY')}
          </Text>
        </div>
      )
    },
    {
      title: 'Appointments',
      key: 'appointments',
      render: (_, record) => (
        <div>
          {record.appointments.map((appointment, index) => (
            <div key={appointment._id} style={{ marginBottom: index < record.appointments.length - 1 ? '8px' : '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Text strong>{moment(appointment.startTime).format('MM/DD/YYYY HH:mm')}</Text>
                <Text>-</Text>
                <Text>{moment(appointment.endTime).format('HH:mm')}</Text>
                <Tag color={appointment.status === 'Scheduled' ? 'blue' : 
                           appointment.status === 'Completed' ? 'green' : 
                           appointment.status === 'Cancelled' ? 'red' : 
                           appointment.status === 'No Show' ? 'orange' : 'default'}>
                  {appointment.status}
                </Tag>
              </div>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary">
                  {appointment.appointmentType} with {appointment.provider?.name || 'N/A'}
                </Text>
                {appointment.reason && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Reason: {appointment.reason}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Total',
      key: 'total',
      width: 80,
      render: (_, record) => (
        <Tag color="cyan">{record.appointments.length}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical">
          {record.appointments.map(appointment => (
            <Space key={appointment._id}>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => showModal(appointment)}
                disabled={!canModify}
              />
              {canModify && (
                <Button
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={() => handleDelete(appointment._id)}
                />
              )}
            </Space>
          ))}
        </Space>
      )
    }
  ];

  // Restore the handleDelete function
  const handleDelete = async (appointmentId) => {
    try {
      await axios.delete(`/appointments/${appointmentId}`);
      message.success('Appointment deleted successfully!');
      fetchAppointments(); // Refresh list
    } catch (err) {
      console.error("Error deleting appointment:", err);
      message.error(err.response?.data?.message || 'Failed to delete appointment.');
    }
  };

  // --- Search Patients ---
  const handlePatientSearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setPatientOptions(allPatients); // Show all patients when no search text
      return;
    }
      
    // First filter from existing allPatients list for instant response
    const filteredPatients = allPatients.filter(patient => 
      patient.label.toLowerCase().includes(searchText.toLowerCase())
    );
    setPatientOptions(filteredPatients);

    // Then debounce search for more results if needed
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(setTimeout(async () => {
      try {
        const response = await axios.get(`/patients?search=${searchText}&limit=20`);
        const patientsFound = response.data.data.patients.map(p => ({
          value: p._id,
          label: `${p.lastName}${p.middleName ? ' ' + p.middleName : ''} ${p.firstName} (DOB: ${moment(p.dateOfBirth).format('YYYY-MM-DD')})`
        }));
        setPatientOptions(patientsFound);
      } catch (searchErr) {
        console.error("Patient search failed:", searchErr);
        setPatientOptions(filteredPatients); // Fallback to filtered list
      }
    }, 300));
  };

  // --- Search Providers ---
  const handleProviderSearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setProviderOptions(allProviders); // Show all providers when no search text
      return;
    }
     
    // First filter from existing allProviders list for instant response
    const filteredProviders = allProviders.filter(provider => 
      provider.label.toLowerCase().includes(searchText.toLowerCase())
    );
    setProviderOptions(filteredProviders);

    // Then debounce search for more results if needed
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(setTimeout(async () => {
      try {
        const response = await axios.get(`/users?search=${searchText}&role=MD&role=Nursing&limit=20`);
        const providersFound = response.data.data.users.map(u => ({
          value: u._id,
          label: `${u.name} (${u.role})`
        }));
        setProviderOptions(providersFound);
      } catch (searchErr) {
        console.error("Provider search failed:", searchErr);
        setProviderOptions(filteredProviders); // Fallback to filtered list
      }
    }, 300));
  };


  const showModal = (appointment = null) => {
    setEditingAppointment(appointment);

    if (appointment) {
      // Pre-fill form for editing
      // Initialize options with full lists
      let patientOptionsToSet = [...allPatients];
      let providerOptionsToSet = [...allProviders];

      // Ensure current patient is in the options list
      if (appointment.patient) {
        const patientLabel = `${appointment.patient.lastName}${appointment.patient.middleName ? ' ' + appointment.patient.middleName : ''} ${appointment.patient.firstName} (DOB: ${moment(appointment.patient.dateOfBirth).format('YYYY-MM-DD')})`;
        const currentPatientOption = {
          value: appointment.patient._id,
          label: patientLabel
        };

        // Check if current patient is already in the list, if not add it
        const patientExists = patientOptionsToSet.some(p => p.value === appointment.patient._id);
        if (!patientExists) {
          patientOptionsToSet.unshift(currentPatientOption);
        }
      }

      // Ensure current provider is in the options list
      if (appointment.provider) {
        const currentProviderOption = {
          value: appointment.provider._id,
          label: `${appointment.provider.name} (${appointment.provider.role || 'Provider'})`
        };

        // Check if current provider is already in the list, if not add it
        const providerExists = providerOptionsToSet.some(p => p.value === appointment.provider._id);
        if (!providerExists) {
          providerOptionsToSet.unshift(currentProviderOption);
        }
      }

      setPatientOptions(patientOptionsToSet);
      setProviderOptions(providerOptionsToSet);

      form.setFieldsValue({
        ...appointment,
        patient: appointment.patient._id, // Store just the ID
        provider: appointment.provider._id, // Store just the ID
        patientDisplay: `${appointment.patient.lastName}${appointment.patient.middleName ? ' ' + appointment.patient.middleName : ''} ${appointment.patient.firstName} (DOB: ${moment(appointment.patient.dateOfBirth).format('YYYY-MM-DD')})`, // Display name
        providerDisplay: `${appointment.provider.name} (${appointment.provider.role || 'Provider'})`, // Display name
        appointmentTime: [
            appointment.startTime ? moment(appointment.startTime) : null,
            appointment.endTime ? moment(appointment.endTime) : null
        ]
      });
    } else {
      // Initialize options with full lists for new appointments
      setPatientOptions(allPatients);
      setProviderOptions(allProviders);
      // Reset form for adding
      form.resetFields();
      form.setFieldsValue({ status: 'Scheduled' }); // Default status
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingAppointment(null);
    form.resetFields();
  };

  const handleFinish = async (values) => {
     if (!user || !user._id) {
        message.error('User not logged in.');
        return;
    }
    
    // Validate required fields before submission
    if (!values.patient || !values.provider || !values.appointmentTime || values.appointmentTime.length !== 2) {
      message.error('Please fill in all required fields: Patient, Provider, and Appointment Time.');
      return;
    }

    setSubmitLoading(true);
    setError(null);

    try {
      // Ensure proper data formatting for backend
      const payload = {
        patient: values.patient, // Already just the ID from AutoComplete value
        provider: values.provider, // Already just the ID from AutoComplete value  
        startTime: values.appointmentTime[0] ? values.appointmentTime[0].toISOString() : null,
        endTime: values.appointmentTime[1] ? values.appointmentTime[1].toISOString() : null,
        appointmentType: values.appointmentType || '',
        status: values.status || 'Scheduled',
        reason: values.reason || '',
        notes: values.notes || '',
      };

      // Additional validation for dates
      if (!payload.startTime || !payload.endTime) {
        message.error('Please select valid start and end times.');
        return;
      }

      if (new Date(payload.startTime) >= new Date(payload.endTime)) {
        message.error('End time must be after start time.');
        return;
      }

      console.log('Submitting appointment payload:', payload); // Debug logging

      let response;
      if (editingAppointment) {
        response = await axios.patch(`/appointments/${editingAppointment._id}`, payload);
        message.success('Appointment updated successfully!');
      } else {
        response = await axios.post('/appointments', payload);
        message.success('Appointment scheduled successfully!');
      }

      console.log('Appointment save response:', response.data); // Debug logging
      
      handleCancel();
      fetchAppointments(); // Refresh list
    } catch (err) {
      console.error("Error saving appointment:", err);
      
      // Detailed error handling
      if (err.response) {
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
        message.error(`Failed to save appointment: ${err.response.data?.message || err.response.data?.error || err.message}`);
      } else if (err.request) {
        console.error("Request error:", err.request);
        message.error('Network error: Unable to reach server. Please check your connection.');
      } else {
        console.error("General error:", err.message);
        message.error(`Error: ${err.message}`);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

   // --- Calendar Event Handlers ---
   const handleSelectEvent = (event) => {
     // Open the edit modal when an event is clicked
     showModal(event.resource); // Pass the original appointment data stored in resource
   };

   const handleSelectSlot = ({ start, end }) => {
     // Optional: Open the add modal when a slot is clicked/dragged
     // Pre-fill start/end times
     console.log('Selected slot:', start, end);
     // showModal(null); // Open Add modal
     // addForm.setFieldsValue({ appointmentTime: [moment(start), moment(end)] });
     message.info('Click "Schedule Appointment" button to create a new event.'); // Placeholder action
   };


  // Check if user has permission to add/edit/delete
  const canModify = user && ['Customer Service', 'Admin', 'Nursing'].includes(user.role);

  return (
    <Card title="Appointments">
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      
      {/* Search and Filter Section */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong>Search Patient:</Text>
              <Search
                placeholder="Search by patient name..."
                value={patientSearchText}
                onChange={(e) => handlePatientSearchChange(e.target.value)}
                allowClear
                enterButton={<SearchOutlined />}
                style={{ marginTop: 4 }}
              />
            </div>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <div>
              <Text strong>Filter by Provider:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Providers"
                value={providerFilter}
                onChange={handleProviderFilterChange}
                allowClear
                onClear={() => setProviderFilter('all')}
              >
                <Option value="all">All Providers</Option>
                {availableProviders.map(provider => (
                  <Option key={provider.id} value={provider.id}>
                    {provider.name} {provider.role && `(${provider.role})`}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={6} md={4}>
            <div>
              <Text strong>Filter by Status:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Statuses"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                allowClear
                onClear={() => setStatusFilter('all')}
              >
                <Option value="all">All Statuses</Option>
                <Option value="Scheduled">Scheduled</Option>
                <Option value="Checked In">Checked In</Option>
                <Option value="Completed">Completed</Option>
                <Option value="Cancelled">Cancelled</Option>
                <Option value="No Show">No Show</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
              <Space>
                {searchAndFilterApplied && (
                  <Button
                    icon={<ClearOutlined />}
                    onClick={clearAllFilters}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                )}
                <Text type="secondary">
                  Showing {filteredAppointments.length} of {appointments.length} appointments
                </Text>
              </Space>
            </div>
          </Col>
        </Row>
        
        {/* View Mode Toggle */}
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Text strong>View Mode:</Text>
              <Radio.Group value={viewMode} onChange={handleViewModeChange}>
                <Radio.Button value="calendar">
                  <CalendarOutlined /> Calendar View
                </Radio.Button>
                <Radio.Button value="patient">
                  <UserOutlined /> Patient View
                </Radio.Button>
              </Radio.Group>
            </div>
          </Col>
        </Row>
      </Card>

      {canModify && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showModal(null)}
          style={{ marginBottom: 16 }}
        >
          Schedule Appointment
        </Button>
      )}
      <Spin spinning={loading}>
        {viewMode === 'calendar' ? (
          // Enhanced Calendar with proper positioning and event display
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            allDayAccessor="allDay"
            style={{ height: 600 }} // Adjust height as needed
            onSelectEvent={handleSelectEvent} // Handle clicking an existing event
            onSelectSlot={handleSelectSlot} // Handle clicking/dragging on empty slots
            selectable={true} // Allow slot selection
            defaultView="week" // Default view (month, week, day, agenda)
            views={['month', 'week', 'day', 'agenda']} // Views to allow switching between
            step={15} // 15-minute time slots
            timeslots={4} // 4 slots per hour (15 minutes each)
            min={new Date(1970, 1, 1, 6, 0, 0)} // Start time at 6:00 AM
            max={new Date(1970, 1, 1, 22, 0, 0)} // End time at 10:00 PM
            scrollToTime={new Date(1970, 1, 1, 8, 0, 0)} // Auto-scroll to 8:00 AM
            dayLayoutAlgorithm="no-overlap" // Prevent event overlapping
            showMultiDayTimes={true}
            popup={true} // Show popup on event hover
            popupOffset={{ x: 30, y: 20 }}
            // Custom event styling based on status
            eventPropGetter={(event) => {
              let backgroundColor = '#1890ff'; // Default blue
              let color = 'white';

              switch (event.status) {
                case 'Scheduled':
                  backgroundColor = '#1890ff'; // Blue
                  break;
                case 'Checked In':
                  backgroundColor = '#faad14'; // Orange
                  break;
                case 'Completed':
                  backgroundColor = '#52c41a'; // Green
                  break;
                case 'Cancelled':
                  backgroundColor = '#ff4d4f'; // Red
                  break;
                case 'No Show':
                  backgroundColor = '#d9d9d9'; // Gray
                  color = '#666';
                  break;
                default:
                  backgroundColor = '#1890ff'; // Default blue
              }

              return {
                style: {
                  backgroundColor,
                  color,
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  padding: '2px 4px'
                }
              };
            }}
            // Custom component for displaying event content with time and status
            components={{
              event: ({ event }) => (
                <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
                  <div style={{ fontWeight: 'bold' }}>
                    {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                  </div>
                  <div>{event.resource?.patient?.lastName}{event.resource?.patient?.middleName ? ' ' + event.resource?.patient?.middleName : ''} {event.resource?.patient?.firstName}</div>
                  <div style={{ opacity: 0.8 }}>
                    Provider: {event.resource?.provider?.name || 'N/A'}
                  </div>
                  <div style={{ opacity: 0.8 }}>
                    {event.status} • {event.appointmentType}
                  </div>
                </div>
              )
            }}
          />
        ) : (
          // Patient View
          <Table
            columns={patientViewColumns}
            dataSource={getPatientGroupedAppointments()}
            rowKey={(record) => record.patient._id}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} patients`
            }}
            style={{ marginTop: 16 }}
            scroll={{ x: true }}
          />
        )}
      </Spin>

      {/* Add/Edit Appointment Modal (Remains the same) */}
      <Modal
        title={editingAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={700}
      >
        <Spin spinning={submitLoading}>
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            {/* Hidden field to store patient ID */}
            <Form.Item name="patient" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="patientDisplay"
              label="Patient"
              rules={[{ required: true, message: 'Please select a patient!' }]}
            >
               <AutoComplete
                    options={patientOptions}
                    onSearch={handlePatientSearch}
                    onFocus={() => setPatientOptions(allPatients)} // Show all patients when focused
                    placeholder="Search for patient by name..."
                    allowClear
                    filterOption={false} // Disable default filtering since we handle search
                    showSearch
                    dropdownMatchSelectWidth={false}
                    style={{ width: '100%' }}
                    onSelect={(value, option) => {
                      // Store the ID in hidden field, display the label in AutoComplete
                      form.setFieldValue('patient', option.value); // Store ID
                      form.setFieldValue('patientDisplay', option.label); // Store display name
                    }}
                    onClear={() => {
                      form.setFieldValue('patient', undefined);
                      form.setFieldValue('patientDisplay', undefined);
                    }}
                />
            </Form.Item>
            {/* Hidden field to store provider ID */}
            <Form.Item name="provider" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="providerDisplay"
              label="Provider"
              rules={[{ required: true, message: 'Please select a provider!' }]}
            >
               <AutoComplete
                    options={providerOptions}
                    onSearch={handleProviderSearch}
                    onFocus={() => setProviderOptions(allProviders)} // Show all providers when focused
                    placeholder="Search for provider by name..."
                    allowClear
                    filterOption={false} // Disable default filtering since we handle search
                    showSearch
                    dropdownMatchSelectWidth={false}
                    style={{ width: '100%' }}
                    onSelect={(value, option) => {
                      // Store the ID in hidden field, display the label in AutoComplete
                      form.setFieldValue('provider', option.value); // Store ID
                      form.setFieldValue('providerDisplay', option.label); // Store display name
                    }}
                    onClear={() => {
                      form.setFieldValue('provider', undefined);
                      form.setFieldValue('providerDisplay', undefined);
                    }}
                />
            </Form.Item>
             <Form.Item
                name="appointmentTime"
                label="Appointment Time"
                rules={[{ required: true, message: 'Please select start and end time!' }]}
             >
                <RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
             </Form.Item>
             <Form.Item
                name="appointmentType"
                label="Appointment Type"
                rules={[{ required: true, message: 'Please select appointment type!' }]}
             >
                <Select 
                  placeholder="Select appointment type..."
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {appointmentTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
             </Form.Item>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  <Select>
                    <Option value="Scheduled">Scheduled</Option>
                    <Option value="Checked In">Checked In</Option>
                    <Option value="Completed">Completed</Option>
                    <Option value="Cancelled">Cancelled</Option>
                    <Option value="No Show">No Show</Option>
                  </Select>
              </Form.Item>
             <Form.Item name="reason" label="Reason (Optional)">
              <Input />
            </Form.Item>
            <Form.Item name="notes" label="Scheduling Notes (Optional)">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingAppointment ? 'Save Changes' : 'Schedule Appointment'}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
};

export default AppointmentScheduler;
